import clsx from 'clsx';
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  cnDate,
  fmtDateWeek,
  fmtMonthDay,
  type AttachmentDTO,
  type FollowUpDTO,
  type MemberDTO,
  type ProjectDetailDTO,
  type ProjectStatus,
  type ProjectTimelineEntry,
} from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { errorText } from '../../lib/api';
import { uploadFile } from '../../lib/attachments';
import { timeCN, todayCN } from '../../lib/clock';
import { useHotkeys } from '../../lib/hotkeys';
import { useLayout } from '../../lib/media';
import { useMe, useMembers, useProject, useProjectActions, useProjects } from '../../lib/queries';
import { AttachButton, AttachmentList } from '../../ui/attachments';
import { Icon, StatusIcon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { RichText } from '../../ui/RichText';
import { AIAvatar, Avatar, avatarHue, Banner, Button, EmptyState, IconButton, Kbds, Skeleton } from '../../ui/primitives';
import { lastRadarOrder } from '../radar/RadarPage';
import { ProjectFormModal } from './ProjectFormModal';
import './project.css';

// 项目详情（设计稿 8a）：动态由日报原文组成，AI 追问以评论线程出现；右侧属性栏含日期变更记录。

const STATUS_LABEL: Record<ProjectStatus, string> = { todo: '未开始', progress: '进行中', blocked: '阻塞', done: '已完成', cancelled: '已取消' };
type Filter = 'all' | 'report' | 'ai';

export function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'mobile';
  const { data, isLoading, error } = useProject(id);
  const { data: all = [] } = useProjects();
  const [filter, setFilter] = useState<Filter>('all');
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);

  const order = useMemo(() => {
    const fromRadar = lastRadarOrder.filter((x) => all.some((p) => p.id === x));
    return fromRadar.includes(id ?? '') ? fromRadar : all.filter((p) => p.visible && p.status !== 'cancelled').map((p) => p.id);
  }, [all, id]);
  const idx = order.indexOf(id ?? '');
  const go = (d: number) => {
    const next = order[idx + d];
    if (next) navigate(`/projects/${next}`, { replace: true });
  };
  // 直接打开链接时没有上一页，回到项目列表
  const back = () => ((window.history.state as { idx?: number } | null)?.idx ? navigate(-1) : navigate('/projects'));
  useHotkeys(
    {
      j: () => go(1),
      k: () => go(-1),
      Escape: (e) => {
        const t = e.target as HTMLElement;
        if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') t.blur();
        else back();
      },
    },
    !mobile,
  );

  if (isLoading) {
    return (
      <div style={{ padding: 40 }} aria-busy="true">
        <Skeleton w={80} h={12} />
        <Skeleton w={260} h={22} style={{ marginTop: 10 }} />
      </div>
    );
  }
  if (error || !data) {
    return <EmptyState icon="grid" title="没找到这个项目" desc={error ? errorText(error) : undefined} />;
  }

  const p = data.project;
  // 名称、负责人、团队、参与人在「编辑项目」里改；其他属性在右侧属性栏里改
  const editModal = editing && (
    <ProjectFormModal
      project={{ id: p.id, code: p.code, name: p.name, ownerId: p.ownerId, teamId: p.teamId, participantIds: data.participants.map((x) => x.id) }}
      onClose={() => setEditing(false)}
    />
  );

  if (mobile) {
    return (
      <div className="pd-m">
        {editModal}
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="pd-back" onClick={back}>
            <Icon name="chevL" size={18} color="var(--text-3)" />
            <span>{p.teamName ?? '项目'}</span>
          </button>
          {data.canEdit && <IconButton mobile name="edit" label="编辑项目" onClick={() => setEditing(true)} />}
        </div>
        <MainColumn data={data} filter={filter} setFilter={setFilter} mobile extra={<Properties data={data} mobile />} />
      </div>
    );
  }

  return (
    <>
      <TopBar crumbs={['项目', p.teamName ?? '未分配团队', p.code]}>
        {copied && <span className="t-meta" style={{ marginRight: 8 }}>已复制链接</span>}
        {idx >= 0 && (
          <span className="t-meta" style={{ marginRight: 10 }}>
            {idx + 1} / {order.length}
          </span>
        )}
        <IconButton name="chevU" label="上一个（K）" disabled={idx <= 0} onClick={() => go(-1)} />
        <IconButton name="chevD" label="下一个（J）" disabled={idx < 0 || idx >= order.length - 1} onClick={() => go(1)} />
        <span className="wd-vsep" />
        <IconButton
          name="link"
          label="复制链接"
          onClick={() => {
            void navigator.clipboard?.writeText(location.href).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        />
        {data.canEdit && (
          <>
            <span className="wd-vsep" />
            <Button icon="edit" onClick={() => setEditing(true)}>
              编辑项目
            </Button>
          </>
        )}
      </TopBar>
      {editModal}
      <div className="pd">
        <MainColumn data={data} filter={filter} setFilter={setFilter} mobile={false} />
        {layout === 'full' || layout === 'noPanel' ? <Properties data={data} /> : null}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// 主列：标题、简介、动态、评论框
// ---------------------------------------------------------------------------

function MainColumn({
  data,
  filter,
  setFilter,
  mobile,
  extra,
}: {
  data: ProjectDetailDTO;
  filter: Filter;
  setFilter: (f: Filter) => void;
  mobile: boolean;
  /** 手机上属性栏插在简介和动态之间 */
  extra?: ReactNode;
}) {
  const p = data.project;
  const entries = data.timeline.filter((e) => filter === 'all' || (filter === 'report' ? e.kind === 'report' : e.kind === 'follow_up' && e.followUp.kind === 'ai'));
  // 发出评论后滚到最新一条；首次打开停在标题处
  const scrollRef = useRef<HTMLDivElement>(null);
  const [sent, setSent] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (sent && el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [sent, data.timeline.length]);
  // 设计稿 19a：撤回后改过的日报，可以展开看修改前后
  const hasRevisions = data.timeline.some((e) => e.kind === 'report' && e.revision);
  const [showRevisions, setShowRevisions] = useState(false);
  return (
    <div className="pd-col">
      <div className="pd-scroll" ref={scrollRef}>
        <div className="pd-in">
          <div className="t-meta">{p.code}</div>
          <h1 className="t-page-title" style={{ marginTop: 6 }}>
            {p.name}
          </h1>
          {data.canView ? (
            <Description data={data} />
          ) : (
            <div style={{ marginTop: 12 }}>
              <Banner icon="lock">你不在这个项目里，只能看到基本信息和与你有关的讨论。需要参与的话，请{data.owner.name}把你加进来。</Banner>
            </div>
          )}
          {extra}

          <div className="pd-tabs">
            <span className="t-group">动态</span>
            <span className="flex1" />
            {(
              [
                ['all', '全部'],
                ['report', '仅日报'],
                ['ai', '仅 AI 追问'],
              ] as const
            ).map(([k, label]) => (
              <button key={k} type="button" className={clsx(filter === k && 'on')} onClick={() => setFilter(k)}>
                {label}
              </button>
            ))}
            {hasRevisions && (
              <>
                <span className="wd-vsep" style={{ height: 12, margin: '0 2px' }} />
                <button type="button" className={clsx(showRevisions && 'on')} onClick={() => setShowRevisions(!showRevisions)} aria-pressed={showRevisions}>
                  显示修改记录
                </button>
              </>
            )}
          </div>

          <div className="pd-tl">
            {entries.length > 0 && <div className="pd-tl-line" />}
            {entries.length === 0 && (
              <div className="t-meta" style={{ padding: '18px 0' }}>
                {data.canView ? '还没有动态。项目成员提交日报后，进展会出现在这里。' : '没有和你有关的动态。'}
              </div>
            )}
            {entries.map((e, i) => (
              <Entry key={`${e.kind}-${e.at}-${i}`} e={e} code={p.code} showRevision={showRevisions} />
            ))}
          </div>
        </div>
      </div>
      {data.canComment && <Composer
        data={data}
        mobile={mobile}
        onSent={() => {
          if (filter !== 'all') setFilter('all');
          setSent((n) => n + 1);
        }}
      />}
    </div>
  );
}

function Description({ data }: { data: ProjectDetailDTO }) {
  const { update } = useProjectActions(data.project.id);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(data.project.description ?? '');
  useEffect(() => setText(data.project.description ?? ''), [data.project.description]);
  if (editing) {
    return (
      <div className="pd-desc-edit">
        <textarea
          className="wd-textarea"
          rows={3}
          autoFocus
          value={text}
          placeholder="一两句话：这个项目要做成什么，和谁有关"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setEditing(false);
            }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              update.mutate({ description: text });
              setEditing(false);
            }
          }}
        />
        <div className="row" style={{ gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="text" onClick={() => setEditing(false)}>
            取消
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              update.mutate({ description: text });
              setEditing(false);
            }}
          >
            保存
          </Button>
        </div>
      </div>
    );
  }
  return (
    <p
      className={clsx('pd-desc', data.canEdit && 'editable', !data.project.description && 'empty')}
      onClick={() => data.canEdit && setEditing(true)}
      title={data.canEdit ? '点击编辑' : undefined}
    >
      {data.project.description ?? (data.canEdit ? '写一两句话介绍这个项目…' : '')}
    </p>
  );
}

/** 名字用和头像同一个色相，长时间线里一眼分清谁是谁 */
function hueStyle(name: string) {
  return { ['--av-h' as string]: avatarHue(name) } as React.CSSProperties;
}

function whenOf(iso: string): string {
  const d = cnDate(new Date(iso));
  return d === todayCN() ? `今天 ${timeCN(iso)}` : `${fmtMonthDay(d)} ${timeCN(iso)}`;
}

function Entry({ e, code, showRevision }: { e: ProjectTimelineEntry; code: string; showRevision: boolean }) {
  switch (e.kind) {
    case 'report':
      return (
        <div className="pd-ev" style={hueStyle(e.member.name)}>
          <Avatar initial={e.member.initial} seed={e.member.name} size={20} />
          <div className="body">
            <div className="head">
              <span className="name tinted">{e.member.name}</span>
              <span className="pd-kind">日报</span>
              <span className="meta">
                {fmtDateWeek(e.date)}
                {e.revision ? ' · 已修改' : ''}
              </span>
            </div>
            <div className={clsx('text', e.noProgress && 'muted')}>
              <RichText text={e.text} />
            </div>
            <AttachmentList files={e.attachments ?? []} />
            {e.revision && showRevision && (
              <div className="pd-diff">
                <div className="h">
                  {code} 这一条有改动
                  <span className="c4">
                    {' · '}
                    {[e.revision.submittedAt && `提交 ${timeCN(e.revision.submittedAt)}`, `撤回 ${timeCN(e.revision.retractedAt)}`, e.revision.resubmittedAt && `重新提交 ${timeCN(e.revision.resubmittedAt)}`]
                      .filter(Boolean)
                      .join(' → ')}
                  </span>
                </div>
                <div className="r">
                  <span className="k">原文</span>
                  <span className="old">{e.revision.original}</span>
                </div>
                <div className="r">
                  <span className="k">现在</span>
                  <span>{e.text}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    case 'due_change':
      return (
        <div className="pd-ev small">
          <span className="pd-dot">
            <Icon name="cal" size={14} />
          </span>
          <span className="ellipsis" title={e.reason ? `理由：${e.reason}` : undefined}>
            <span className="c2">{e.byName}</span> 把截止日从 {e.from ? <s className="c4">{fmtMonthDay(e.from)}</s> : '未定'} 改为 {fmtMonthDay(e.to)}
          </span>
          <span className="c4 nowrap">· {fmtMonthDay(cnDate(new Date(e.at)))}</span>
        </div>
      );
    case 'status':
      return (
        <div className="pd-ev small">
          <span className="pd-dot">
            <StatusIcon status={e.to} />
          </span>
          <span className="ellipsis">
            {e.byName ? (
              <>
                <span className="c2">{e.byName}</span> 把状态从 {STATUS_LABEL[e.from]} 改为 {STATUS_LABEL[e.to]}
              </>
            ) : (
              <>根据日报，状态从 {STATUS_LABEL[e.from]} 变为 {STATUS_LABEL[e.to]}</>
            )}
          </span>
          <span className="c4 nowrap">· {fmtMonthDay(cnDate(new Date(e.at)))}</span>
        </div>
      );
    case 'follow_up':
      return <Thread f={e.followUp} aboutName={e.aboutName} />;
    case 'comment':
      return (
        <div className="pd-ev" style={hueStyle(e.author.name)}>
          <Avatar initial={e.author.initial} seed={e.author.name} size={20} />
          <div className="body pd-bubble">
            <div className="head">
              <span className="name tinted">{e.author.name}</span>
              <span className="meta">{whenOf(e.at)}</span>
            </div>
            {!(e.text === '（附件）' && e.attachments?.length) && (
              <div className="text">
                <RichText text={e.text} />
              </div>
            )}
            <AttachmentList files={e.attachments ?? []} />
          </div>
        </div>
      );
  }
}

function Thread({ f, aboutName }: { f: FollowUpDTO; aboutName: string }) {
  const { data: members = [] } = useMembers();
  const answerer = members.find((m) => m.id === f.toId);
  return (
    <div className="pd-ev" style={f.kind === 'ai' ? undefined : hueStyle(f.fromName)}>
      {f.kind === 'ai' ? <AIAvatar size={20} /> : <Avatar initial={f.fromInitial} seed={f.fromName} size={20} />}
      <div className="pd-thread">
        <div className="q">
          <div className="head">
            <span className={clsx('name', f.kind !== 'ai' && 'tinted')}>{f.kind === 'ai' ? 'AI 追问' : `${f.fromName}的追问`}</span>
            <span className="meta">
              {whenOf(f.askedAt)} · 针对{aboutName}的{f.kind === 'ai' ? '日报' : '项目进展'}
            </span>
          </div>
          <div className="text">
            <RichText text={f.question} />
          </div>
        </div>
        <div className="sep" />
        {f.answer ? (
          <div className="a" style={hueStyle(f.toName)}>
            <Avatar initial={answerer?.initial ?? f.toName.slice(0, 1)} seed={f.toName} size={18} />
            <div style={{ minWidth: 0 }}>
              <div className="head">
                <span className="name tinted">{f.toName}</span>
                <span className="meta">
                  {f.answeredAt ? timeCN(f.answeredAt) : ''}
                  {f.answerChannel === 'wechat' ? ' · 来自微信' : ''}
                </span>
              </div>
              <div className="text">
                <RichText text={f.answer} />
              </div>
            </div>
          </div>
        ) : (
          <div className="a t-meta">{f.skipped ? `${f.toName}跳过了这条追问` : `等待${f.toName}回复`}</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 评论框：@ 某人追问
// ---------------------------------------------------------------------------

function Composer({ data, mobile, onSent }: { data: ProjectDetailDTO; mobile: boolean; onSent: () => void }) {
  const { data: me } = useMe();
  const { data: members = [] } = useMembers();
  const { comment } = useProjectActions(data.project.id);
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [hl, setHl] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<AttachmentDTO[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const addFiles = async (list: File[]) => {
    setUploading(true);
    setError(null);
    try {
      for (const f of list) {
        setProgress(0);
        const a = await uploadFile(f, undefined, setProgress);
        setFiles((x) => [...x, a]);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  // 光标前是“@xx”时弹出成员候选：项目成员排前面
  const mention = dismissed ? null : text.slice(0, caret).match(/@([^\s@]*)$/);
  const inProject = new Set([data.owner.id, ...data.participants.map((m) => m.id)]);
  const candidates: MemberDTO[] = mention
    ? members
        .filter((m) => m.id !== me?.id && m.status !== 'left' && m.name.includes(mention[1]))
        .sort((x, y) => Number(inProject.has(y.id)) - Number(inProject.has(x.id)))
        .slice(0, 6)
    : [];

  const pick = (m: MemberDTO) => {
    if (!mention) return;
    const start = caret - mention[0].length;
    const next = `${text.slice(0, start)}@${m.name} ${text.slice(caret)}`;
    const pos = start + m.name.length + 2;
    setText(next);
    setCaret(pos);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(pos, pos);
    });
  };

  const canSend = (!!text.trim() || files.length > 0) && !uploading;
  const send = async () => {
    if (!canSend || comment.isPending) return;
    try {
      await comment.mutateAsync({ text: text.trim(), attachmentIds: files.map((f) => f.id) });
      setText('');
      setFiles([]);
      setError(null);
      onSent();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const sync = (el: HTMLInputElement) => setCaret(el.selectionStart ?? el.value.length);

  return (
    <div className={clsx('pd-composer', mobile && 'mobile')}>
      <div className="pd-in row" style={{ gap: 12 }}>
        {!mobile && <Avatar initial={me?.initial ?? '我'} seed={me?.name} size={20} />}
        <div className="box" ref={boxRef}>
          <input
            ref={inputRef}
            value={text}
            placeholder="写评论，或 @ 某人追问…"
            enterKeyHint="send"
            onChange={(e) => {
              setText(e.target.value);
              sync(e.target);
              setHl(0);
              setDismissed(false);
              setError(null);
            }}
            onClick={(e) => sync(e.currentTarget)}
            onKeyUp={(e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') sync(e.currentTarget);
            }}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (candidates.length) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHl((h) => (e.key === 'ArrowDown' ? Math.min(h + 1, candidates.length - 1) : Math.max(h - 1, 0)));
                  return;
                }
                if ((e.key === 'Enter' && !e.metaKey && !e.ctrlKey) || e.key === 'Tab') {
                  e.preventDefault();
                  pick(candidates[Math.min(hl, candidates.length - 1)]);
                  return;
                }
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setDismissed(true);
                  return;
                }
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey || mobile)) {
                e.preventDefault();
                void send();
              }
            }}
            aria-label="评论"
          />
          <AttachButton count={files.length} busy={uploading} progress={progress} onFiles={(l) => void addFiles(l)}>
            {mobile ? '' : undefined}
          </AttachButton>
          {!mobile && <Kbds keys={['⌘', '↵']} />}
          <Button variant="primary" mobile={mobile} disabled={!canSend || comment.isPending} onClick={() => void send()}>
            评论
          </Button>
        </div>
      </div>
      {files.length > 0 && <AttachmentList className="pd-pending-files" files={files} onRemove={(id) => setFiles((x) => x.filter((f) => f.id !== id))} />}
      <Popover anchor={boxRef} open={candidates.length > 0} onClose={() => setDismissed(true)} width={240}>
        <div className="wd-pop-label">提到某人，对方会在收件箱看到</div>
        <div className="wd-pop-list" role="listbox">
          {candidates.map((m, i) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={i === hl}
              className={clsx('wd-pop-item', i === hl && 'hl')}
              onMouseEnter={() => setHl(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(m)}
            >
              <Avatar initial={m.initial} seed={m.name} />
              <span className="flex1 ellipsis">{m.name}</span>
              <span className="t-meta">{inProject.has(m.id) ? '项目成员' : (m.teamName ?? '')}</span>
            </button>
          ))}
        </div>
      </Popover>
      {error && (
        <div className="t-meta c-red" style={{ marginTop: 6, paddingLeft: mobile ? 0 : 32 }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 属性栏（可编辑：负责人、负责人的主管、管理者）
// ---------------------------------------------------------------------------

function Properties({ data, mobile }: { data: ProjectDetailDTO; mobile?: boolean }) {
  const navigate = useNavigate();
  const p = data.project;
  const blocked = p.health.find((h) => h.kind === 'blocked');
  const slips = data.dueChanges.filter((c) => c.from && c.to > c.from).length;
  return (
    <aside className={clsx('pd-panel', mobile && 'mobile')}>
      <div>
        <StatusProp data={data} blockedLabel={blocked?.label.replace('阻塞 ', '') ?? null} />
        <div className="pd-kv">
          <span className="k">负责人</span>
          <span className="v">
            <button type="button" className="pd-person" onClick={() => navigate(`/people/${data.owner.id}`)}>
              <Avatar initial={data.owner.initial} seed={data.owner.name} />
              {data.owner.name}
            </button>
          </span>
        </div>
        {data.participants.length > 0 && (
          <div className="pd-kv">
            <span className="k">参与人</span>
            <span className="v">
              <span className="pd-stack">
                {data.participants.slice(0, 4).map((m) => (
                  <Avatar key={m.id} initial={m.initial} seed={m.name} />
                ))}
              </span>
              <span className="ellipsis">
                {data.participants.map((m, i) => (
                  <Fragment key={m.id}>
                    {i > 0 && '、'}
                    <button type="button" className="pd-name" onClick={() => navigate(`/people/${m.id}`)}>
                      {m.name}
                    </button>
                  </Fragment>
                ))}
              </span>
            </span>
          </div>
        )}
        <CustomerProp data={data} />
        <PhaseProp data={data} />
        <NextStepProp data={data} />
        <DueProp data={data} slips={mobile ? slips : 0} />
      </div>

      {data.dueChanges.length > 0 && !mobile && (
        <>
          <div className="hr" />
          <div>
            <div className="pd-label">
              <span className="name">日期变更</span>
              <span className="flex1" />
              {slips > 0 && <span className="c-red">截止日已后延 {slips} 次</span>}
            </div>
            {data.dueChanges.map((c, i) => (
              <div key={i} className="pd-change">
                <div className="row" style={{ gap: 6, fontSize: 13 }}>
                  {c.from ? <s className="c4">{fmtMonthDay(c.from)}</s> : <span className="c4">未定</span>}
                  <span className="c4">→</span>
                  <span className={i === 0 ? 'c1' : 'c2'}>{fmtMonthDay(c.to)}</span>
                </div>
                <span className="t-meta">
                  {c.byName} · {fmtMonthDay(cnDate(new Date(c.at)))}
                  {c.reason ? ` · 理由：${c.reason}` : ''}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {(data.dependents.length > 0 || data.dependsOn.length > 0) && (
        <>
          <div className="hr" />
          {data.dependents.length > 0 && (
            <div>
              <div className="pd-label">
                <span className="name">被依赖</span>
              </div>
              {data.dependents.map((d) => (
                <button key={d.id} type="button" className="pd-link" onClick={() => navigate(`/projects/${d.id}`)}>
                  <StatusIcon status={d.status} />
                  <span className="t-meta">{d.code}</span>
                  <span className="c1 ellipsis">{d.name}</span>
                </button>
              ))}
            </div>
          )}
          {data.dependsOn.length > 0 && (
            <div>
              <div className="pd-label">
                <span className="name">依赖</span>
              </div>
              {data.dependsOn.map((d) => (
                <button key={d.id} type="button" className="pd-link" onClick={() => navigate(`/projects/${d.id}`)}>
                  <StatusIcon status={d.status} />
                  <span className="t-meta">{d.code}</span>
                  <span className="c1 ellipsis">{d.name}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </aside>
  );
}

/** 可点击编辑的一行属性 */
function EditableKV({
  k,
  canEdit,
  children,
  editor,
  width = 260,
  align,
}: {
  k: string;
  canEdit: boolean;
  children: ReactNode;
  editor: (close: () => void) => ReactNode;
  width?: number;
  align?: 'start';
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button ref={ref} type="button" className={clsx('pd-kv', canEdit && 'editable', align === 'start' && 'top')} disabled={!canEdit} onClick={() => setOpen(!open)}>
        <span className="k">{k}</span>
        <span className="v">{children}</span>
      </button>
      <Popover anchor={ref} open={open} onClose={() => setOpen(false)} width={width}>
        {editor(() => setOpen(false))}
      </Popover>
    </>
  );
}

function StatusProp({ data, blockedLabel }: { data: ProjectDetailDTO; blockedLabel: string | null }) {
  const { update } = useProjectActions(data.project.id);
  const p = data.project;
  return (
    <EditableKV
      k="状态"
      canEdit={data.canEdit}
      width={200}
      editor={(close) => (
        <MenuList
          items={(Object.keys(STATUS_LABEL) as ProjectStatus[]).map((s) => ({ value: s, label: STATUS_LABEL[s], icon: <StatusIcon status={s} /> }))}
          selected={p.status}
          onPick={(s) => {
            if (s !== p.status) update.mutate({ status: s });
            close();
          }}
        />
      )}
    >
      <StatusIcon status={p.status} />
      <span className="c1">{STATUS_LABEL[p.status]}</span>
      {blockedLabel && <span className="c-red" style={{ fontSize: 12 }}>{blockedLabel}</span>}
    </EditableKV>
  );
}

function CustomerProp({ data }: { data: ProjectDetailDTO }) {
  const { update } = useProjectActions(data.project.id);
  const value = data.project.customer;
  if (!value && !data.canEdit) return null;
  return (
    <EditableKV
      k="客户"
      canEdit={data.canEdit}
      editor={(close) => (
        <TextForm
          initial={value ?? ''}
          placeholder="客户名称"
          onSave={(v) => {
            update.mutate({ customer: v || null });
            close();
          }}
          onCancel={close}
        />
      )}
    >
      <span className={clsx('ellipsis', value ? 'c1' : 'c4')}>{value ?? '未设置'}</span>
    </EditableKV>
  );
}

function TextForm({ initial, placeholder, onSave, onCancel }: { initial: string; placeholder: string; onSave: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(initial);
  return (
    <form
      className="wd-pop-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(v.trim());
      }}
    >
      <input className="wd-input" autoFocus value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} />
      <div className="actions">
        <Button variant="text" onClick={onCancel}>
          取消
        </Button>
        <Button variant="primary" type="submit">
          保存
        </Button>
      </div>
    </form>
  );
}

function PhaseProp({ data }: { data: ProjectDetailDTO }) {
  const { update } = useProjectActions(data.project.id);
  const p = data.project;
  return (
    <EditableKV
      k="阶段"
      canEdit={data.canEdit}
      editor={(close) => (
        <PhaseForm
          name={p.phase ?? ''}
          index={(p.phaseIndex ?? 0) + 1}
          count={p.phaseCount ?? 4}
          onSave={(name, index, count) => {
            update.mutate({ phase: name || null, phaseIndex: index - 1, phaseCount: count });
            close();
          }}
          onCancel={close}
        />
      )}
    >
      <span className={p.phase ? 'c1' : 'c4'}>{p.phase ?? '未设置'}</span>
      {p.phaseCount ? <span className="t-meta">{(p.phaseIndex ?? 0) + 1} / {p.phaseCount}</span> : null}
    </EditableKV>
  );
}

function PhaseForm({ name, index, count, onSave, onCancel }: { name: string; index: number; count: number; onSave: (n: string, i: number, c: number) => void; onCancel: () => void }) {
  const [n, setN] = useState(name);
  const [i, setI] = useState(index);
  const [c, setC] = useState(count);
  return (
    <form
      className="wd-pop-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(n.trim(), Math.min(i, c), c);
      }}
    >
      <input className="wd-input" autoFocus value={n} placeholder="阶段名称，例如：资料补充" onChange={(e) => setN(e.target.value)} />
      <div className="row" style={{ gap: 8, fontSize: 12, color: 'var(--text-4)' }}>
        <span>第</span>
        <input className="wd-input" type="number" min={1} max={c} value={i} onChange={(e) => setI(Number(e.target.value) || 1)} style={{ width: 56 }} />
        <span>步，共</span>
        <input className="wd-input" type="number" min={1} max={12} value={c} onChange={(e) => setC(Number(e.target.value) || 1)} style={{ width: 56 }} />
        <span>步</span>
      </div>
      <div className="actions">
        <Button variant="text" onClick={onCancel}>
          取消
        </Button>
        <Button variant="primary" type="submit">
          保存
        </Button>
      </div>
    </form>
  );
}

function NextStepProp({ data }: { data: ProjectDetailDTO }) {
  const { update } = useProjectActions(data.project.id);
  const { data: members = [] } = useMembers();
  const ns = data.project.nextStep;
  if (!ns && !data.canEdit) return null;
  return (
    <EditableKV
      k="下一步"
      canEdit={data.canEdit}
      align="start"
      width={280}
      editor={(close) => (
        <NextStepForm
          members={members}
          initial={ns}
          onSave={(v) => {
            update.mutate({ nextStep: v });
            close();
          }}
          onCancel={close}
        />
      )}
    >
      {ns ? (
        <span className="col" style={{ gap: 2, minWidth: 0 }}>
          <span className="c1" style={{ lineHeight: '18px' }}>
            {ns.text}
          </span>
          <span className="t-meta">{[ns.date ? fmtDateWeek(ns.date) : null, data.nextStepAssignee?.name].filter(Boolean).join(' · ')}</span>
        </span>
      ) : (
        <span className="c4">未设置</span>
      )}
    </EditableKV>
  );
}

function NextStepForm({
  members,
  initial,
  onSave,
  onCancel,
}: {
  members: MemberDTO[];
  initial: { text: string; date: string | null; assigneeId: string | null } | null;
  onSave: (v: { text: string; date: string | null; assigneeId: string | null } | null) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial?.text ?? '');
  const [date, setDate] = useState(initial?.date ?? '');
  const [who, setWho] = useState(initial?.assigneeId ?? '');
  return (
    <form
      className="wd-pop-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(text.trim() ? { text: text.trim(), date: date || null, assigneeId: who || null } : null);
      }}
    >
      <input className="wd-input" autoFocus value={text} placeholder="下一步要做什么" onChange={(e) => setText(e.target.value)} />
      <div className="row" style={{ gap: 8 }}>
        <input className="wd-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="日期" />
        <select className="pd-select" value={who} onChange={(e) => setWho(e.target.value)} aria-label="谁来做" style={{ height: 32 }}>
          <option value="">谁来做</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>
      <div className="actions">
        {initial && (
          <Button variant="text" onClick={() => onSave(null)}>
            清除
          </Button>
        )}
        <span className="flex1" />
        <Button variant="text" onClick={onCancel}>
          取消
        </Button>
        <Button variant="primary" type="submit">
          保存
        </Button>
      </div>
    </form>
  );
}

function DueProp({ data, slips }: { data: ProjectDetailDTO; slips: number }) {
  const { update } = useProjectActions(data.project.id);
  const due = data.project.dueDate;
  return (
    <EditableKV
      k="截止日"
      canEdit={data.canEdit}
      width={280}
      editor={(close) => (
        <DueForm
          current={due}
          saving={update.isPending}
          onSave={async (d, reason) => {
            await update.mutateAsync({ dueDate: d, dueReason: reason });
            close();
          }}
          onCancel={close}
        />
      )}
    >
      <span className={due ? 'c1' : 'c4'}>{due ? fmtDateWeek(due) : '未设置'}</span>
      {slips > 0 && <span className="c-red" style={{ fontSize: 12 }}>后延 {slips} 次</span>}
    </EditableKV>
  );
}

function DueForm({ current, saving, onSave, onCancel }: { current: string | null; saving: boolean; onSave: (d: string, reason: string | null) => Promise<void>; onCancel: () => void }) {
  const [d, setD] = useState(current ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const later = !!current && !!d && d > current;
  return (
    <form
      className="wd-pop-form"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!d) return setError('选一个日期');
        if (later && !reason.trim()) return setError('往后推需要写一句理由');
        try {
          await onSave(d, reason.trim() || null);
        } catch (err) {
          setError(errorText(err));
        }
      }}
    >
      <input className="wd-input" type="date" autoFocus value={d} onChange={(e) => setD(e.target.value)} aria-label="截止日" />
      {later && (
        <>
          <span className="t-meta">往后推 · 会记进日期变更，连续后延会进主管收件箱</span>
          <input className="wd-input" value={reason} placeholder="理由，例如：等 TÜV 回复" onChange={(e) => setReason(e.target.value)} />
        </>
      )}
      {error && <span className="t-meta c-red">{error}</span>}
      <div className="actions">
        <Button variant="text" onClick={onCancel}>
          取消
        </Button>
        <Button variant="primary" type="submit" disabled={saving || d === current}>
          保存
        </Button>
      </div>
    </form>
  );
}
